import { getUser } from "$lib/userstorage/usersotrage";
import { refreshAccessToken } from "$lib/java/auth/refreshSession";
import { isOnline } from "$lib/sync/networkStatus";

function dispatchSessionExpired() {
    window.dispatchEvent(new CustomEvent('session-expired', {
        detail: { message: 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.' }
    }));
}

export async function handleAuthenticatedRequest(url, options) {
    try {
        const response = await fetch(url, options);

        // 401 = access vencido -> intentar renovar con el refresh token.
        if (response.status === 401) {
            // Offline: no tiene sentido refrescar ni desloguear. Lo tratamos
            // como error de red para que el caller sirva cache / reintente.
            if (!isOnline()) {
                throw new Error('SIN_CONEXION');
            }

            const result = await refreshAccessToken(); // single-flight
            if (result === 'ok') {
                // Reintento transparente con el nuevo access.
                const retry = await fetch(url, {
                    ...options,
                    headers: {
                        ...(options?.headers || {}),
                        Authorization: `Bearer ${getUser().token}`,
                    },
                });
                if (retry.status === 401 || retry.status === 403) {
                    dispatchSessionExpired();
                    throw new Error('SESIÓN_EXPIRADA');
                }
                return retry;
            }
            if (result === 'invalid') {
                dispatchSessionExpired();
                throw new Error('SESIÓN_EXPIRADA');
            }
            // result === 'network': el refresh no pudo completarse por red.
            // No deslogueamos; el caller lo trata como falta de conexión.
            throw new Error('SIN_CONEXION');
        }

        // 403 = permiso denegado: NO es sesión vencida -> NO deslogueamos. El
        // caller lo trata como falta de permiso (no manda al login).
        if (response.status === 403) {
            throw new Error('SIN_PERMISO');
        }

        return response;
    } catch (error) {
        throw error;
    }
}
// ¿Este error es "no hay red" y no "el servidor contestó mal"?
//
// Sin conexión el `fetch` se rechaza con un TypeError, y `handleAuthenticatedRequest`
// traduce el 401 sin red a SIN_CONEXION. Un 500, un 404 o un SIN_PERMISO NO son
// esto: los callers que degradan a cache ante falta de red tienen que dejar
// pasar esos como error real en vez de tapar un problema del servidor con datos
// viejos.
export function esFaltaDeConexion(e) {
    if (!isOnline()) return true;
    const msg = String(e?.message ?? "");
    if (msg === "SIN_CONEXION") return true;
    return e instanceof TypeError && /fetch|network|load failed/i.test(msg);
}

// Consume el body de una respuesta OK, o arroja si el status es de error.
//
// `handleAuthenticatedRequest` corta en 401/403, pero DEVUELVE tal cual todo lo
// demás (500 del back, 502 de nginx, 404 de ruta mal). Hacerle `.json()` a eso
// no explota necesariamente: si el back manda un body de error JSON, el caller
// se queda con un objeto que no es la lista esperada y la pantalla lo muestra
// como "no hay nada" — un error de servidor disfrazado de dato vacío.
//
// El Error lleva `.status` para que los callers puedan distinguir "el servidor
// contestó mal" (propagar) de "no hay red" (servir cache); ver `isRealError`
// en permisosback.js y los `isRealAuthError` de usuarios/establecimientos.
//
// Y lleva el `message` y el `code` que mandó el backend, que antes se tiraban:
// quedaba sólo "etiqueta -> 400" y el motivo real no llegaba a ninguna parte. Un
// checkout rechazado por Mercado Pago se veía como un cartel genérico mientras
// el backend explicaba exactamente qué pasaba. Mismo patrón que `saveAnimal`.
//
// Que traiga el mensaje no obliga a mostrarlo: un texto de backend rara vez está
// escrito para el usuario. Sirve para diagnosticar, y para los casos en que sí
// se muestra (`mensajeLimitePlan`).
export async function jsonOrThrow(res, label) {
    if (!res.ok) {
        let body = {};
        // Un error puede no traer JSON —502 de nginx, respuesta vacía— y eso no
        // tiene que tapar el status, que es lo único que siempre sirve.
        try { body = await res.json(); } catch (_) { /* sin cuerpo JSON */ }
        const err = new Error(body?.message || `${label} -> ${res.status}`);
        err.status = res.status;
        err.code = body?.code;   // "PLAN_LIMIT_REACHED" cuando es tope de plan
        err.label = label;       // el `label -> status` de siempre, para el log
        throw err;
    }
    return await res.json();
}

// Mensaje a mostrar cuando el backend rechazó por el tope del plan (409 con
// `code: "PLAN_LIMIT_REACHED"`), o `null` si el error es cualquier otra cosa.
//
// Existe para que los catch de las pantallas no tapen el rechazo por límite con
// su "No se pudo guardar, intentá de nuevo": el tope del plan no se arregla
// intentando de nuevo, y el mensaje del backend ya dice el plan, el máximo y el
// uso actual. Uso: `fail(mensajeLimitePlan(err) ?? "No se pudo guardar el tacto")`.
export function mensajeLimitePlan(err) {
    if (err?.code !== "PLAN_LIMIT_REACHED") return null;
    const detalle = err.message?.trim();
    const base = detalle || "Alcanzaste el límite de tu plan.";
    return `${base} Lo que ya cargaste no se ve afectado.`;
}

export async function handleTransferRequest(url, options) {
    try {
        const response = await fetch(url, options);

        if (response.status === 400 ) {
            throw new Error('transfer');
        }
        if ( response.status === 404) {
            throw new Error('renspa');
        }
        if (!response.ok) {
            throw new Error('transfer');
        }

        return response;
    } catch (error) {
        throw error;
    }
}
export async function handleLoginRequest(url, options) {
    try {
        const response = await fetch(url, options);
        
        if (response.status === 401 ) {


            throw new Error('credenciales');
        }
        if ( response.status === 403) {
            // Una cuenta creada con Google o Apple no tiene contraseña, así que
            // ninguna le va a funcionar: el back lo avisa con
            // `code: NO_PASSWORD_SET`. Se mira el `code` y no el texto de
            // `error`, que es para mostrar, no para parsear.
            const body = await response.json().catch(() => ({}));
            const err = new Error(body.code === 'NO_PASSWORD_SET' ? 'sin_password' : 'eliminado');
            err.body = body;
            throw err;
        }
        // Cualquier otro error (500 del back, 502 de nginx, 404 de ruta mal):
        // cortamos acá. Si lo dejamos pasar, el caller hace `.json()` sobre un
        // body de error, se queda con `token: undefined` y ENTRA IGUAL: sesión
        // sin token que después falla en cada request como si fuera permiso.
        if (!response.ok) {
            const err = new Error('servidor');
            err.status = response.status;
            throw err;
        }

        return response;
    } catch (error) {
        throw error;
    }
}